import { Dispatch } from "redux";
import { User } from "../types/userTypes";
import {
  collection,
  query,
  getDocs,
  doc,
  where,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase";
import { accountRequest } from "../../api/account";
import { markNotificationsAsViewedByUserId } from "./notificationActions";

export const FETCH_USERS_REQUEST = "FETCH_USERS_REQUEST";
export const FETCH_USERS_SUCCESS = "FETCH_USERS_SUCCESS";
export const FETCH_USERS_FAILURE = "FETCH_USERS_FAILURE";
export const FETCH_USERS_RESET = "FETCH_USERS_RESET";

export const fetchUsersRequest = () => ({
  type: FETCH_USERS_REQUEST,
});

export const fetchUsersSuccess = (users: User[]) => ({
  type: FETCH_USERS_SUCCESS,
  payload: users,
});

export const fetchUsersFailure = (error: string) => ({
  type: FETCH_USERS_FAILURE,
  payload: error,
});

export const resetUserState = () => ({
  type: FETCH_USERS_RESET,
  //payload: users,
});

const accountAction =
  (action: string, data: Record<string, string>) =>
  async (dispatch: Dispatch): Promise<boolean> => {
    dispatch(resetUserState());
    dispatch(fetchUsersRequest());
    try {
      const result = await accountRequest(action, data);
      dispatch(fetchUsersSuccess(result.users));
      if (result.notice) dispatch(fetchUsersFailure(result.notice));
      return true;
    } catch (error) {
      dispatch(
        fetchUsersFailure(
          error instanceof Error ? error.message : "Account request failed.",
        ),
      );
      return false;
    }
  };

export const fetchUserByEmailAndPassword = (
  email: string,
  password: string,
  captchaToken = "",
  _limitLoginAttempts = true,
) => accountAction("sign-in", { email, password, captchaToken });

export const createUser = (
  email: string,
  password: string,
  captchaToken = "",
) => accountAction("sign-up", { email, password, captchaToken });

export const fetchUserByUniqueId =
  (id: number) => async (dispatch: Dispatch) => {
    dispatch(fetchUsersRequest());
    try {
      const users: User[] = [];

      /* Query the "users" collection for a document with the specified productUrl. */
      let q = query(collection(db, "users"), where("createdTime", "==", id));

      let docData: User | undefined;
      const querySnapshot = await getDocs(q);
      for (const doc of querySnapshot.docs) {
        docData = doc.data() as User;
        docData.documentId = doc.id;
        users.push(docData);
      }

      dispatch(fetchUsersSuccess(users));
    } catch (error) {
      console.error(error);
      dispatch(fetchUsersFailure("Error fetching items."));
    }
  };

export const confirmUserAccount =
  (id: number, email: string) => async (dispatch: Dispatch) => {
    dispatch(fetchUsersRequest());
    try {
      let users: User[] = [];

      users = await getUniqueUserByEmail(email);
      if (users[0].createdTime === id && !users[0].isVerified) {
        await updateUserById(users[0], { isVerified: true });
        // Auto-clear "Confirm Your Account" notification (userId === createdTime)
        await markNotificationsAsViewedByUserId(id);
      } else {
        users = [];
      }

      dispatch(fetchUsersSuccess(users));
    } catch (error) {
      console.error(error);
      dispatch(fetchUsersFailure("Error fetching items."));
    }
  };

export const fetchUserByEmail =
  (email: string) => async (dispatch: Dispatch) => {
    dispatch(fetchUsersRequest());
    try {
      const users = await getUniqueUserByEmail(email);

      dispatch(fetchUsersSuccess(users));
    } catch (error) {
      console.error(error);
      dispatch(fetchUsersFailure("Error fetching items."));
    }
  };

export const updateUserPassword = (
  email: string,
  currentPassword = "",
  newPassword = "",
  captchaToken = "",
) =>
  newPassword
    ? accountAction("change-password", {
        email,
        currentPassword,
        newPassword,
        captchaToken,
      })
    : accountAction("reset-password", { email, captchaToken });

const getUniqueUserByEmail = async (email: string) => {
  try {
    const users: User[] = [];

    /* Query the "users" collection for a document with the specified productUrl. */
    let q = query(collection(db, "users"), where("email", "==", email));

    let docData: User | undefined;
    const querySnapshot = await getDocs(q);
    querySnapshot.forEach((doc) => {
      docData = doc.data() as User;
      docData.documentId = doc.id;
      users.push(docData);
    });
    return users;
  } catch (error) {
    console.error(error);
    throw new Error("Error fetching users.");
  }
};

const updateUserById = async (user: User, dataToUpdate: any) => {
  try {
    const docRef = doc(db, "users", user.documentId || "");
    await updateDoc(docRef, dataToUpdate);
  } catch (error) {
    console.error("Error updating document: ", error);
  }
};
